import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HOME_ROUTE, findRoute, labelForPath } from '@/routes/routeMap';

const MAX_TABS = 12;
// always-open tabs (cannot be closed), in this order after Home
const PINNED_PATHS = ['/outward', '/reports/stock-matrix', '/reports/item-ledger'];

const TabsContext = createContext(null);

const makeTabId = () => `tab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const buildTab = (path, overrideLabel) => {
  const route = findRoute(path);
  return {
    id: makeTabId(),
    path,
    label: overrideLabel || (route ? route.label : labelForPath(path)),
    icon: route ? route.icon : null,
    stale: false,
    createdAt: Date.now(),
  };
};

export const TabsProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Seed with a single Home tab whose path tracks current URL.
  const [tabs, setTabs] = useState(() => {
    const home = buildTab(location.pathname === '/' ? '/' : '/');
    // Pin home as non-closable
    home.pinned = true;
    home.id = 'tab-home';
    home.label = HOME_ROUTE.label;
    home.icon = HOME_ROUTE.icon;
    const pinned = PINNED_PATHS.filter((p) => findRoute(p)).map((p) => ({ ...buildTab(p), id: `tab-pin-${p}`, pinned: true }));
    // a fresh start shows only Home and the pinned tabs (they open their page when first clicked)
    const list = [home, ...pinned];
    if (location.pathname !== '/' && !list.some((t) => t.path === location.pathname)) {
      if (list.length >= MAX_TABS) list.splice(1 + pinned.length, 1);
      list.push(buildTab(location.pathname));
    }
    return list;
  });
  const [activeId, setActiveId] = useState(() => {
    const deep = tabs.find((t) => t.path === location.pathname);
    return deep ? deep.id : 'tab-home';
  });

  // most-recently-used order, for the Alt+Q switcher (like Alt+Tab)
  const mruRef = useRef([]);
  useEffect(() => {
    mruRef.current = [activeId, ...mruRef.current.filter((id) => id !== activeId)];
  }, [activeId]);

  // --- Helpers ---
  const findTabByPath = useCallback((path) => tabs.find((t) => t.path === path), [tabs]);

  const getActiveTab = useCallback(
    () => tabs.find((t) => t.id === activeId) || tabs[0],
    [tabs, activeId],
  );

  // --- Public actions ---
  const openInNewTab = useCallback((path, labelOverride, { duplicate = false } = {}) => {
    if (!path) return;
    setTabs((prev) => {
      const existing = !duplicate && prev.find((t) => t.path === path);
      if (existing) {
        setActiveId(existing.id);
        return prev;
      }
      // Create a fresh tab; evict oldest non-pinned if at cap
      let next = [...prev];
      if (next.length >= MAX_TABS) {
        const oldestIdx = next.findIndex((t) => !t.pinned);
        if (oldestIdx !== -1) next.splice(oldestIdx, 1);
      }
      const copies = prev.filter((t) => t.path === path).length;
      const fresh = buildTab(path, labelOverride || (copies ? `${(findRoute(path) || {}).label || labelForPath(path)} ${copies + 1}` : undefined));
      next = [...next, fresh];
      setActiveId(fresh.id);
      return next;
    });
    navigate(path);
  }, [navigate]);

  const switchTab = useCallback((id) => {
    const target = tabs.find((t) => t.id === id);
    if (!target) return;
    setActiveId(id);
    // Mark non-stale when user switches back
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, stale: false } : t)));
    if (target.path !== location.pathname) {
      navigate(target.path);
    }
  }, [tabs, navigate, location.pathname]);

  const closeTab = useCallback((id) => {
    setTabs((prev) => {
      const tab = prev.find((t) => t.id === id);
      if (!tab || tab.pinned) return prev;
      const idx = prev.indexOf(tab);
      const next = prev.filter((t) => t.id !== id);
      // If we closed the active tab, move focus to the neighbor on the left
      if (id === activeId) {
        const neighbor = next[Math.max(0, idx - 1)] || next[0];
        if (neighbor) {
          setActiveId(neighbor.id);
          if (neighbor.path !== location.pathname) {
            setTimeout(() => navigate(neighbor.path), 0);
          }
        }
      }
      return next;
    });
  }, [activeId, navigate, location.pathname]);

  // tab ids in most-recently-used order (only open tabs)
  const mruOrder = useCallback(() => {
    const open = new Set(tabs.map((t) => t.id));
    const seen = mruRef.current.filter((id) => open.has(id));
    return [...seen, ...tabs.map((t) => t.id).filter((id) => !seen.includes(id))];
  }, [tabs]);

  // next / previous tab in bar order (swipe on mobile)
  const cycleTab = useCallback((dir) => {
    if (tabs.length < 2) return;
    const i = Math.max(0, tabs.findIndex((t) => t.id === activeId));
    const next = tabs[(i + dir + tabs.length) % tabs.length];
    switchTab(next.id);
  }, [tabs, activeId, switchTab]);

  // a second copy of the current page (e.g. two Stock Matrix tabs to compare)
  const duplicateTab = useCallback((id) => {
    const t = tabs.find((x) => x.id === (id || activeId));
    if (t) openInNewTab(t.path, undefined, { duplicate: true });
  }, [tabs, activeId, openInNewTab]);

  const refreshTab = useCallback((id) => {
    // Bump a refresh counter so the rendered component can use it as a key/dep to re-fetch
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, stale: false, refreshKey: (t.refreshKey || 0) + 1 } : t)));
  }, []);

  // --- URL → tabs sync ---
  // Whenever the URL changes (e.g., user clicks a sidebar Link, or uses back/forward),
  // - if URL matches an already-open tab → switch to it (no new tab created)
  // - else → REPLACE the active tab's path + label (as per UX decision 2.a)
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    const path = location.pathname;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    const active = tabs.find((t) => t.id === activeId);
    if (active && active.path === path) return;
    const existing = tabs.find((t) => t.path === path);
    if (existing) {
      if (existing.id !== activeId) setActiveId(existing.id);
      return;
    }
    if (!active || active.pinned) {
      // pinned tabs keep their page: anything else opens in its own tab
      setTabs((prev) => {
        let next = [...prev];
        if (next.length >= MAX_TABS) {
          const i = next.findIndex((t) => !t.pinned);
          if (i !== -1) next.splice(i, 1);
        }
        const fresh = buildTab(path);
        setActiveId(fresh.id);
        return [...next, fresh];
      });
      return;
    }
    // Replace active tab's path
    setTabs((prev) => prev.map((t) => {
      if (t.id !== activeId) return t;
      const route = findRoute(path);
      return {
        ...t,
        path,
        label: route ? route.label : labelForPath(path),
        icon: route ? route.icon : t.icon,
        stale: false,
        refreshKey: (t.refreshKey || 0) + 1,
      };
    }));
  }, [location.pathname, tabs, activeId]);

  // When user switches tabs, mark OTHER tabs (that aren't currently active) as stale
  // after a short idle delay — UX decision 4.a (manual refresh hint).
  useEffect(() => {
    const timer = setTimeout(() => {
      setTabs((prev) => prev.map((t) => (t.id !== activeId && !t.stale ? { ...t, stale: true } : t)));
    }, 60 * 1000); // mark stale after 60s of being inactive
    return () => clearTimeout(timer);
  }, [activeId]);

  const value = {
    tabs,
    activeId,
    activeTab: getActiveTab(),
    openInNewTab,
    switchTab,
    cycleTab,
    mruOrder,
    duplicateTab,
    closeTab,
    refreshTab,
    findTabByPath,
    MAX_TABS,
  };

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
};

export const useTabs = () => {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('useTabs must be used within TabsProvider');
  return ctx;
};
